import { ChangeDetectionStrategy, Component, OnInit, signal, inject } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { first, tap } from 'rxjs/operators';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';

/**
 * TokenRefreshModalComponent
 *
 * Modal dialog shown to users when their authentication token is about to expire.
 * Gives users the option to:
 * - Continue their session (refresh token)
 * - Logout
 *
 * This component is used by both web and mobile platforms.
 * On mobile, it's displayed as a native-feeling dialog using PrimeNG's DynamicDialog.
 *
 * Based on the production-tested desktop app implementation.
 */
@Component({
    selector: 'myabvv-token-refresh-modal',
    standalone: true,
    templateUrl: './token-refresh-modal.component.html',
    styleUrls: ['./token-refresh-modal.component.css'],
    imports: [TranslatePipe],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class TokenRefreshModalComponent implements OnInit {
  private translateService = inject(TranslateService);
  ref = inject(DynamicDialogRef);
  config = inject(DynamicDialogConfig);


  logoutTranslatedLabel = signal('');
  continueTranslatedLabel = signal('');

  ngOnInit(): void {
    this.translateLabels();
  }

  /**
   * User chose to continue their session
   * Triggers token refresh via the callback provided by the service
   */
  continueSession(): void {
    this.config.data.onContinue();
    this.ref.close();
  }

  /**
   * User chose to logout
   * Triggers logout via the callback provided by the service
   */
  logout(): void {
    this.config.data.onLogout();
    this.ref.close();
  }

  /**
   * Load translated labels for buttons
   * @private
   */
  private translateLabels(): void {
    this.translate('logout');
    this.translate('continue');
  }

  /**
   * Translate a specific label key
   * @private
   */
  private translate(name: string): void {
    const key = 'TOKEN_REFRESH.' + name;
    this.translateService.get(key)
      .pipe(
        first(),
        tap({
          next: value => {
            switch (name) {
              case 'logout':
                this.logoutTranslatedLabel.set(value);
                break;
              case 'continue':
                this.continueTranslatedLabel.set(value);
                break;
              default:
                break;
            }
          },
          error: err => console.error('[TokenRefreshModalComponent] Translation error:', err)
        })
      ).subscribe();
  }
}
